import { apiClient } from '../client';

/**
 * Client for `/api/eos/vto/*`. Stays in lockstep with
 * `server/src/routes/eos/vto.routes.ts` (Stream B Wave 3, ADR-023).
 *
 * The V/TO is stored whole-document per version: every PUT appends a new
 * immutable `EosVtoVersion`; history is append-only.
 */

// ── Document shape ──────────────────────────────────────────────────────

/**
 * Frontend mirror of `server/src/modules/eos/vto/defaults.ts`
 * (`vtoDataSchema`). The two MUST stay in lockstep.
 */
export interface VtoData {
  /** Up to 10 core values, each a short phrase. */
  coreValues: string[];
  coreFocus: {
    purpose: string;
    niche: string;
  };
  tenYearTarget: string;
  marketingStrategy: {
    targetMarket: string;
    threeUniques: string[];
    provenProcess: string;
    guarantee: string;
  };
  threeYearPicture: {
    /** YYYY-MM-DD or '' (date not chosen yet). */
    futureDate: string;
    revenue: string;
    profit: string;
    measurables: string[];
    bullets: string[];
  };
  oneYearPlan: {
    /** YYYY-MM-DD or '' (date not chosen yet). */
    futureDate: string;
    revenue: string;
    profit: string;
    goals: string[];
  };
}

/**
 * The blank document a fresh org starts from — mirror of the server's
 * `EMPTY_VTO_DATA`. The GET endpoint returns `vto: null` before the
 * first save; the page renders this skeleton client-side.
 */
export const EMPTY_VTO_DATA: VtoData = {
  coreValues: [],
  coreFocus: { purpose: '', niche: '' },
  tenYearTarget: '',
  marketingStrategy: { targetMarket: '', threeUniques: [], provenProcess: '', guarantee: '' },
  threeYearPicture: { futureDate: '', revenue: '', profit: '', measurables: [], bullets: [] },
  oneYearPlan: { futureDate: '', revenue: '', profit: '', goals: [] },
};

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/**
 * Defensive normalizer — versions are validated server-side, but the
 * page reads `dataJson` from JSON columns; never let a malformed blob
 * crash the render. Missing sections fall back to the empty shape.
 */
export function normalizeVtoData(raw: unknown): VtoData {
  if (!raw || typeof raw !== 'object') return EMPTY_VTO_DATA;
  const d = raw as Record<string, unknown>;
  const coreFocus = (d.coreFocus ?? {}) as Record<string, unknown>;
  const marketing = (d.marketingStrategy ?? {}) as Record<string, unknown>;
  const threeYear = (d.threeYearPicture ?? {}) as Record<string, unknown>;
  const oneYear = (d.oneYearPlan ?? {}) as Record<string, unknown>;
  return {
    coreValues: asStringArray(d.coreValues),
    coreFocus: { purpose: asString(coreFocus.purpose), niche: asString(coreFocus.niche) },
    tenYearTarget: asString(d.tenYearTarget),
    marketingStrategy: {
      targetMarket: asString(marketing.targetMarket),
      threeUniques: asStringArray(marketing.threeUniques),
      provenProcess: asString(marketing.provenProcess),
      guarantee: asString(marketing.guarantee),
    },
    threeYearPicture: {
      futureDate: asString(threeYear.futureDate),
      revenue: asString(threeYear.revenue),
      profit: asString(threeYear.profit),
      measurables: asStringArray(threeYear.measurables),
      bullets: asStringArray(threeYear.bullets),
    },
    oneYearPlan: {
      futureDate: asString(oneYear.futureDate),
      revenue: asString(oneYear.revenue),
      profit: asString(oneYear.profit),
      goals: asStringArray(oneYear.goals),
    },
  };
}

// ── API shapes ──────────────────────────────────────────────────────────

export interface VtoDoc {
  id: string;
  /** Null = the company V/TO. */
  departmentId: string | null;
}

export interface VtoVersion {
  id: string;
  vtoId: string;
  version: number;
  /** Whole-document snapshot — run through `normalizeVtoData` to read. */
  dataJson: unknown;
  changeSummary: string | null;
  createdByMembershipId: string | null;
  createdAt: string;
}

/** History rows skip `dataJson` — fetch the full version to render it. */
export type VtoHistoryEntry = Omit<VtoVersion, 'vtoId' | 'dataJson'>;

export interface GetVtoResponse {
  vto: VtoDoc | null;
  current: VtoVersion | null;
  history: VtoHistoryEntry[];
}

export async function getVto(departmentId?: string): Promise<GetVtoResponse> {
  const params = new URLSearchParams();
  if (departmentId) params.set('departmentId', departmentId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: GetVtoResponse }>(`/api/eos/vto${query}`);
  return res.data.data;
}

export async function getVtoVersion(versionId: string): Promise<VtoVersion> {
  const res = await apiClient.get<{ data: VtoVersion }>(
    `/api/eos/vto/versions/${encodeURIComponent(versionId)}`,
  );
  return res.data.data;
}

export interface SaveVtoRequest {
  /** Omit/null = the company V/TO. */
  departmentId?: string | null;
  dataJson: VtoData;
  changeSummary?: string;
}

/** Appends a new version — gated on `manage_eos` server-side. */
export async function saveVto(body: SaveVtoRequest): Promise<{ vto: VtoDoc; current: VtoVersion }> {
  const res = await apiClient.put<{ data: { vto: VtoDoc; current: VtoVersion } }>('/api/eos/vto', body);
  return res.data.data;
}
